// -- [App root - admin detection, MarketingPage or AdminEditor]

import { useState, useEffect, lazy, Suspense } from 'react'
import configData from './content/config.json'
import type { ContentConfig } from './types/content'
import { MarketingPage } from './components/MarketingPage'
import { TryPage } from './pages/TryPage'
import { fetchCurrentData, fetchPublicPricingInfo } from './lib/currentDataApi'
import { mergeFaqWithPublicPricing, mergePricingConfigWithPublicInfo } from './lib/pricingConfig'

// -- [Admin loaded only when ?admin=1 or #admin to keep main bundle smaller]
const AdminEditor = lazy(() => import('./admin/AdminEditor').then((m) => ({ default: m.AdminEditor })))

// -- [Export for console: window.resetDriviblePreferences()]
import { resetPreferences } from './lib/content'
if (typeof window !== 'undefined') {
  (window as unknown as { resetDriviblePreferences?: () => void }).resetDriviblePreferences =
    resetPreferences
}

function isAdminMode(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  if (params.get('admin') === '1') return true
  if (window.location.hash === '#admin') return true
  return false
}

function isTryPath(): boolean {
  if (typeof window === 'undefined') return false
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  if (path === '/try') return true
  // -- [/try/{code} serves the same page with the code prefilled from the path]
  return /^\/try\/[^/]+$/.test(path)
}

export default function App() {
  const [admin, setAdmin] = useState(false)
  const [config, setConfig] = useState<ContentConfig>(() => structuredClone(configData as ContentConfig))

  useEffect(() => {
    setAdmin(isAdminMode())
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault()
        setAdmin((a) => !a)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // -- [Hydrate pricing from API while keeping config.json defaults as resilient fallback]
  useEffect(() => {
    let cancelled = false

    async function syncPricing() {
      const currentDataResult = await fetchCurrentData({ includePricingInfo: true })
      let pricingInfo =
        currentDataResult.ok && currentDataResult.data.pricingInfo
          ? currentDataResult.data.pricingInfo
          : null

      if (!pricingInfo) {
        const pricingResult = await fetchPublicPricingInfo()
        if (pricingResult.ok) pricingInfo = pricingResult.data
      }

      if (!pricingInfo || cancelled) return
      setConfig((prev) => {
        const mergedPricing = mergePricingConfigWithPublicInfo(prev.pricing, pricingInfo)
        const mergedFaq = mergeFaqWithPublicPricing(prev.faq, pricingInfo)
        return {
          ...prev,
          pricing: mergedPricing ?? prev.pricing,
          faq: mergedFaq,
        }
      })
    }

    syncPricing()
    return () => {
      cancelled = true
    }
  }, [])

  if (admin) {
    return (
      <div>
        <div className="fixed top-2 right-2 z-[200] px-3 py-1 bg-amber-500 text-white text-sm font-medium rounded-md">
          Admin Mode
        </div>
        <Suspense fallback={<div className="p-8 text-center text-gray-500">Loading admin…</div>}>
          <AdminEditor initialConfig={config} />
        </Suspense>
      </div>
    )
  }

  if (isTryPath()) {
    return <TryPage />
  }

  return <MarketingPage config={config} />
}
